/**
 * Utility class for handling Tizen app / installer versions.
 *
 * Versions follow vMAJOR.MINOR.PATCH with an optional pre-release suffix and order as:
 *   5.18.1-dev.3  <  5.18.1-rc.0  <  5.18.1  <  5.18.2
 * A leading "v" is ignored and legacy numeric-only values ("4.41") still compare correctly.
 */
type ParsedVersion = { core: number[]; pre: string | null; preNumber: number };

const PRE_RELEASE_ORDER: Record<string, number> = { dev: 0, rc: 1 };

export default class TizenAppsVersionUtil {
  static getVersionString(version: string | number): string {
    return typeof version === 'number' ? String(version) : version;
  }

  static parse(version: string | number): ParsedVersion {
    const raw = this.getVersionString(version).trim().replace(/^v/i, '');
    const dash = raw.indexOf('-');
    const coreStr = dash === -1 ? raw : raw.slice(0, dash);
    const preStr = dash === -1 ? '' : raw.slice(dash + 1);
    const core = coreStr.split('.').map((p) => parseInt(p, 10) || 0);
    if (!preStr) {
      return { core, pre: null, preNumber: 0 };
    }
    const preDot = preStr.indexOf('.');
    const pre = (preDot === -1 ? preStr : preStr.slice(0, preDot)).toLowerCase();
    const preNumber = preDot === -1 ? 0 : parseInt(preStr.slice(preDot + 1), 10) || 0;
    return { core, pre, preNumber };
  }

  /**
   * Returns a negative number if version1 < version2, 0 if equal,
   * a positive number if version1 > version2.
   * Pre-release ordering: dev < rc < release (no suffix); same label orders by its number.
   */
  static compareVersions(version1: string | number, version2: string | number): number {
    const a = this.parse(version1);
    const b = this.parse(version2);
    const length = Math.max(a.core.length, b.core.length);
    for (let i = 0; i < length; i++) {
      const diff = (a.core[i] || 0) - (b.core[i] || 0);
      if (diff !== 0) {
        return diff;
      }
    }
    if (a.pre === b.pre) {
      return a.preNumber - b.preNumber;
    }
    // A release outranks any pre-release of the same core version.
    if (a.pre === null) {
      return 1;
    }
    if (b.pre === null) {
      return -1;
    }
    const rankA = PRE_RELEASE_ORDER[a.pre] ?? -1;
    const rankB = PRE_RELEASE_ORDER[b.pre] ?? -1;
    return rankA !== rankB ? rankA - rankB : a.pre.localeCompare(b.pre);
  }

  static compare(version1: string | number, version2: string | number): boolean {
    return this.compareVersions(version1, version2) === 0;
  }

  static greaterThan(version1: string | number, version2: string | number): boolean {
    return this.compareVersions(version1, version2) > 0;
  }

  static difference(version1: string | number, version2: string | number): number {
    return this.compareVersions(version1, version2);
  }

  static lessThan(version1: string | number, version2: string | number): boolean {
    return this.compareVersions(version1, version2) < 0;
  }
}
